import React, { useContext, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiArrowRight,
  FiGift,
  FiMinus,
  FiPlus,
  FiTrash2,
} from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData, {
  formatCurrency,
  siteConfig,
} from "../Datas/productData";
import "../css/cartpage.scss";

const CartPage = () => {
  const navigate = useNavigate();
  const {
    cartItems,
    removeFromCart,
    updateQuantity,
    clearCart,
    orderNote,
    updateOrderNote,
  } = useContext(CartContext);

  const populatedItems = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = populatedItems.reduce(
    (total, item) => total + item.product.price * item.quantity,
    0
  );
  const shipping =
    subtotal >= siteConfig.shipping.freeThreshold
      ? 0
      : siteConfig.shipping.standardRate;
  const amountToFreeShipping = Math.max(
    0,
    siteConfig.shipping.freeThreshold - subtotal
  );
  const shippingProgress = Math.min(
    100,
    (subtotal / siteConfig.shipping.freeThreshold) * 100
  );

  useEffect(() => {
    document.title = `Cart — ${siteConfig.brandName}`;
  }, []);

  const proceedToCheckout = () => {
    navigate("/checkout");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (populatedItems.length === 0) {
    return (
      <main className="cart-empty" id="main-content">
        <div className="cart-empty__card">
          <span className="cart-empty__icon">
            <FiGift aria-hidden="true" />
          </span>
          <p className="eyebrow">Your paper trail starts here</p>
          <h1>Your cart is empty</h1>
          <p>
            Explore cheerful cards, tiny notes, and playful paper goods made to
            brighten someone’s day.
          </p>
          <Link className="button" to="/shop">
            Continue shopping <FiArrowRight aria-hidden="true" />
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="cart-page" id="main-content">
      <section className="cart-wrap" aria-labelledby="cart-heading">
        <p className="eyebrow">Almost ready to delight</p>
        <h1 id="cart-heading">Cart</h1>
        <span className="page-divider" />

        <div className="cart-shipping-meter">
          <p>
            {amountToFreeShipping > 0
              ? `Add ${formatCurrency(
                  amountToFreeShipping
                )} more for free standard shipping.`
              : "You unlocked free standard shipping!"}
          </p>
          <div aria-hidden="true">
            <span style={{ width: `${shippingProgress}%` }} />
          </div>
        </div>

        <div className="cart-table">
          <div className="cart-table__head" aria-hidden="true">
            <span>Item</span>
            <span>Price</span>
            <span>Quantity</span>
          </div>

          <div className="cart-items">
            {populatedItems.map((item) => {
              const key = `${item.id}-${item.size}-${item.color}`;
              return (
                <article className="cart-item" key={key}>
                  <div className="cart-item__product">
                    <button
                      className="cart-item__remove"
                      type="button"
                      onClick={() =>
                        removeFromCart(item.id, item.size, item.color)
                      }
                      aria-label={`Remove ${item.product.name}`}
                    >
                      <FiTrash2 aria-hidden="true" />
                    </button>
                    <Link to={`/shopDetails/${item.product.id}`}>
                      <img
                        src={item.product.images[0]}
                        alt={item.product.name}
                      />
                    </Link>
                    <div>
                      <p className="cart-item__category">
                        {item.product.category}
                      </p>
                      <Link
                        className="cart-item__name"
                        to={`/shopDetails/${item.product.id}`}
                      >
                        {item.product.name}
                      </Link>
                      {item.size && (
                        <p className="cart-item__option">Format: {item.size}</p>
                      )}
                      {item.color && (
                        <p className="cart-item__option">
                          Included: {item.color}
                        </p>
                      )}
                    </div>
                  </div>

                  <p className="cart-item__unit-price">
                    {formatCurrency(item.product.price)}
                  </p>

                  <div className="cart-item__quantity">
                    <div className="cart-quantity-control">
                      <button
                        type="button"
                        onClick={() =>
                          item.quantity === 1
                            ? removeFromCart(item.id, item.size, item.color)
                            : updateQuantity(
                                item.id,
                                item.size,
                                item.color,
                                item.quantity - 1
                              )
                        }
                        aria-label="Decrease quantity"
                      >
                        <FiMinus />
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        type="button"
                        onClick={() =>
                          updateQuantity(
                            item.id,
                            item.size,
                            item.color,
                            item.quantity + 1
                          )
                        }
                        aria-label="Increase quantity"
                      >
                        <FiPlus />
                      </button>
                    </div>
                    <strong>
                      {formatCurrency(item.product.price * item.quantity)}
                    </strong>
                  </div>
                </article>
              );
            })}
          </div>
        </div>

        <div className="cart-actions-row">
          <div>
            <Link className="text-link" to="/shop">
              <FiArrowLeft aria-hidden="true" /> Continue shopping
            </Link>
            <button type="button" onClick={clearCart}>
              Clear cart
            </button>
          </div>
          <p>
            <span>Subtotal</span>
            <strong>{formatCurrency(subtotal)}</strong>
          </p>
        </div>

        <div className="cart-bottom">
          <div className="cart-note">
            <label htmlFor="order-note">A note for our studio (optional)</label>
            <textarea
              id="order-note"
              value={orderNote}
              onChange={(event) => updateOrderNote(event.target.value)}
              placeholder="Gift message, delivery note, or anything else we should know."
              maxLength="300"
            />
          </div>

          <aside className="cart-summary">
            <div>
              <span>Subtotal</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            <div>
              <span>Standard shipping</span>
              <span>{shipping === 0 ? "Free" : formatCurrency(shipping)}</span>
            </div>
            <p>
              <span>Total</span>
              <strong>{formatCurrency(subtotal + shipping)}</strong>
            </p>
            <small>Taxes are calculated during checkout.</small>
            <button type="button" onClick={proceedToCheckout}>
              Checkout <FiArrowRight aria-hidden="true" />
            </button>
          </aside>
        </div>
      </section>
    </main>
  );
};

export default CartPage;
